import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { supabase, signIn as supabaseSignIn, signOut as supabaseSignOut } from '@/lib/supabase'
import type { User, Session } from '@supabase/supabase-js'
import type { StaffMember } from '@/types'
import { isManagerRole } from '@/lib/constants'
import { ActivityLogger } from '@/lib/activityLogger'

interface AuthState {
  user: User | null
  session: Session | null
  staffMember: StaffMember | null
  isLoading: boolean
  error: string | null
  isAuthenticated: boolean
  isManager: boolean

  // Actions
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  setUser: (user: User | null) => void
  setSession: (session: Session | null) => void
  setStaffMember: (staff: StaffMember | null) => void
  setError: (error: string | null) => void
  initialize: () => Promise<void>
  fetchStaffMember: (email: string) => Promise<boolean>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      staffMember: null,
      isLoading: true,
      error: null,
      isAuthenticated: false,
      isManager: false,

      signIn: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const { user, session } = await supabaseSignIn(email, password)
          set({ user, session })

          // Fetch staff member details and verify manager access
          const hasAccess = await get().fetchStaffMember(email)

          if (!hasAccess) {
            // Sign out if not a valid manager
            await supabaseSignOut()
            const errorMsg = get().error || 'Access denied'
            set({
              user: null,
              session: null,
              isAuthenticated: false,
              isManager: false
            })
            throw new Error(errorMsg)
          }

          set({ isAuthenticated: true })

          // Log login activity
          const staffMember = get().staffMember
          if (staffMember) {
            ActivityLogger.login(`${staffMember.first_name} ${staffMember.last_name || ''}`.trim())
          }
        } catch (error: any) {
          set({ error: error.message || 'Failed to sign in', isAuthenticated: false })
          throw error
        } finally {
          set({ isLoading: false })
        }
      },

      signOut: async () => {
        set({ isLoading: true, error: null })
        try {
          // Log logout activity before clearing state
          const staffMember = get().staffMember
          if (staffMember) {
            ActivityLogger.logout(`${staffMember.first_name} ${staffMember.last_name || ''}`.trim())
          }

          await supabaseSignOut()
          set({
            user: null,
            session: null,
            staffMember: null,
            isAuthenticated: false,
            isManager: false,
          })
        } catch (error: any) {
          set({ error: error.message || 'Failed to sign out' })
        } finally {
          set({ isLoading: false })
        }
      },

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setSession: (session) => set({ session }),
      setStaffMember: (staff) => set({
        staffMember: staff,
        isManager: isManagerRole(staff?.role)
      }),
      setError: (error) => set({ error }),

      fetchStaffMember: async (email: string): Promise<boolean> => {
        // Distinguish "definitively not authorized" from "transient gateway hiccup".
        // PGRST002 (schema cache rebuilding after a migration), 503, 521, 502, and
        // network failures are TRANSIENT — signing the user out on those is what
        // caused the dashboard to randomly drop sessions every time we shipped
        // an RPC migration. Only PGRST116 (= no rows / .single() found 0) and
        // PGRST301 (= JWT expired) mean "definitely not this user".
        const isTransient = (err: any): boolean => {
          if (!err) return false
          const code = String(err.code || '')
          const msg = String(err.message || '')
          const status = Number(err.status || 0)
          if (code === 'PGRST002') return true             // schema cache rebuild
          if (code === 'PGRST500') return true             // postgrest crash
          if (status >= 500 && status < 600) return true   // any 5xx
          if (status === 0 && /failed to fetch|network/i.test(msg)) return true
          if (/Could not query the database/i.test(msg)) return true
          return false
        }

        const MAX_ATTEMPTS = 4
        let lastErr: any = null
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
          try {
            // Self read, resolved from the SESSION rather than from an email the
            // client supplied. staff_self_profile is scoped by staff_my_id(), so
            // it can only ever return the caller's own row.
            const { data, error } = await supabase
              .from('staff_self_profile')
              .select('*')
              .single()

            if (!error && data) {
              const staffData = data as StaffMember
              const hasManagerAccess = isManagerRole(staffData.role)
              if (!hasManagerAccess) {
                set({
                  staffMember: staffData,
                  isManager: false,
                  error: 'Access denied. Only managers can access this dashboard.'
                })
                return false
              }
              set({ staffMember: staffData, isManager: true, error: null })
              return true
            }

            // Definitive not-authorized: PGRST116 means .single() found 0 rows.
            // Anything else with an error is treated transient and retried.
            if (error && error.code === 'PGRST116') {
              console.error('Staff member not found by email:', error)
              set({
                staffMember: null,
                isManager: false,
                error: 'No staff member found with this email. Only registered staff can access the dashboard.'
              })
              return false
            }

            lastErr = error
            if (!isTransient(error)) {
              console.error('Staff member lookup failed (non-transient):', error)
              break
            }

            // Transient — back off and retry (250ms, 500ms, 1s)
            console.warn(`[Auth] Transient staff_members error (attempt ${attempt}/${MAX_ATTEMPTS}):`, error?.code || error?.message)
            await new Promise(r => setTimeout(r, 250 * attempt))
          } catch (error: any) {
            lastErr = error
            if (!isTransient(error)) {
              console.error('Error fetching staff member (non-transient):', error)
              break
            }
            console.warn(`[Auth] Transient staff_members exception (attempt ${attempt}/${MAX_ATTEMPTS}):`, error?.message)
            await new Promise(r => setTimeout(r, 250 * attempt))
          }
        }

        // All attempts exhausted on a transient class of error. CRITICAL: do NOT
        // clear staffMember/isManager here — keep the persisted session alive so
        // the user stays signed in across the gateway hiccup. The session itself
        // is still valid; only the post-auth metadata lookup failed. On the next
        // page-load the persisted state restores from Zustand and another retry
        // attempt happens via the visibility-change listener.
        const persisted = get().staffMember
        if (persisted && (persisted.email || '').toLowerCase() === email.toLowerCase()) {
          console.warn('[Auth] Staff lookup transient-failed; keeping persisted session', lastErr?.code || lastErr?.message)
          set({ error: null })
          return true   // treat as still-authorized for this tick
        }

        // No persisted state to fall back on — show a friendly error but don't
        // hard-clear (the parent caller in initialize() handles UI state).
        console.error('[Auth] Staff lookup failed with no persisted fallback:', lastErr)
        set({
          staffMember: null,
          isManager: false,
          error: 'Failed to verify staff member. Please retry.'
        })
        return false
      },

      initialize: async () => {
        const currentState = get()

        // If already fully initialized with valid session, skip
        if (currentState.session && currentState.isAuthenticated && currentState.isManager) {
          console.log('[Auth] Already has valid session, skipping init')
          set({ isLoading: false })
          return
        }

        // For returning users with persisted staffMember: show UI immediately
        const hasPersisted = currentState.staffMember && currentState.isAuthenticated
        if (hasPersisted) {
          console.log('[Auth] Has persisted state, loading UI while restoring session...')
          set({ isLoading: false })
        } else {
          set({ isLoading: true })
        }

        try {
          console.log('[Auth] Restoring session...')
          const { data: { session }, error } = await supabase.auth.getSession()

          if (error) {
            console.warn('[Auth] getSession error:', error.message)
            // Token refresh failed (timeout, network error, expired)
            // Clear everything and force re-login
            set({
              isLoading: false,
              isAuthenticated: false,
              user: null,
              session: null,
              staffMember: null,
              isManager: false,
            })
            return
          }

          if (session?.user) {
            console.log('[Auth] Session restored for:', session.user.email)
            set({ user: session.user, session })

            if (hasPersisted) {
              console.log('[Auth] Session + persisted state ready')
              return
            }

            // Fresh login - fetch staff member
            if (session.user.email) {
              const hasAccess = await get().fetchStaffMember(session.user.email)
              if (hasAccess) {
                set({ isAuthenticated: true })
              } else {
                await supabaseSignOut()
                set({
                  user: null,
                  session: null,
                  isAuthenticated: false,
                  isManager: false,
                  staffMember: null
                })
              }
            } else {
              set({ isAuthenticated: false })
            }
          } else {
            console.log('[Auth] No valid session')
            set({
              isAuthenticated: false,
              user: null,
              session: null,
              staffMember: null,
              isManager: false,
            })
          }
        } catch (error: any) {
          console.error('[Auth] Init error:', error?.message || error)
          // Auth fetch timed out or network failed - force re-login
          set({
            isLoading: false,
            isAuthenticated: false,
            user: null,
            session: null,
            staffMember: null,
            isManager: false,
          })
        } finally {
          set({ isLoading: false })
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        staffMember: state.staffMember
      }),
    }
  )
)

// Set up auth state listener
// CRITICAL: Do NOT await Supabase calls inside this callback!
// Doing so creates a deadlock because all Supabase calls internally acquire
// the same lock that onAuthStateChange is holding. See:
// https://github.com/supabase/auth-js/issues/762
// https://github.com/supabase/supabase-js/issues/1594
supabase.auth.onAuthStateChange((event, session) => {
  const store = useAuthStore.getState()

  if (event === 'SIGNED_IN' && session?.user) {
    store.setUser(session.user)
    store.setSession(session)
    // Defer async Supabase work to avoid deadlock
    if (session.user.email) {
      setTimeout(async () => {
        const hasAccess = await store.fetchStaffMember(session.user.email!)
        // 2026-05-26 — fetchStaffMember now returns `true` on transient failures
        // when there's a persisted staffMember to fall back on, so this branch
        // only fires for DEFINITIVE not-authorized (PGRST116 / wrong role).
        // Do NOT sign out on transient PGRST002 / 503 / network errors here —
        // the user has a valid Supabase session; only the post-auth metadata
        // lookup blipped. Forcing signOut() in that case was the reason the
        // dashboard kept dropping sessions every time we shipped an RPC migration.
        if (!hasAccess) {
          await supabaseSignOut()
          store.setUser(null)
          store.setSession(null)
          store.setStaffMember(null)
        }
      }, 0)
    }
  } else if (event === 'SIGNED_OUT') {
    store.setUser(null)
    store.setSession(null)
    store.setStaffMember(null)
  }
})
